/**
 * After a deploy, a page that was opened before it asks for chunks of the old build. Their
 * names carry a content hash, so they are gone, and the lazy import fails. Reloading once picks
 * up the new build; a second failure within a minute is a real problem and is shown (and
 * reported) instead of reloading again.
 */
const KEY = 'plsys-stale-build-reload-at';
const WINDOW_MS = 60_000;

const PATTERNS = [
  /Failed to fetch dynamically imported module/, // Chrome
  /error loading dynamically imported module/, // Firefox
  /Importing a module script failed/, // Safari
  /Unable to preload CSS/, // Vite, for the CSS of a chunk
];

/** Whether `error` is a chunk of an older build that no longer exists. */
export function isStaleBuildError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Reloads the page unless it already did so within the last minute. Returns true when a reload
 * has started (the caller shows nothing and reports nothing).
 */
let reloading = false;

export function reloadForNewBuild(now = Date.now()): boolean {
  // React may render an error screen more than once for one error: the reload that started
  // stays the answer for this page, instead of counting as a second failure.
  if (reloading) return true;
  try {
    const last = Number(sessionStorage.getItem(KEY) ?? '0');
    if (now - last < WINDOW_MS) return false;
    sessionStorage.setItem(KEY, String(now));
  } catch {
    return false; // No storage: a loop could not be stopped, so do not reload by ourselves.
  }
  reloading = true;
  window.location.reload();
  return true;
}
